/**
 * Styles for PrivacyAccountDeletionModal (danger zone only, inside BaseModal).
 */

import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'
import type { MenuSheetLayout } from '../../../src/layout/helpers/menuSheetLayout.helper'

export const getPrivacyAccountDeletionModalStyles = (
  theme: Theme,
  layout: MenuSheetLayout
) =>
  StyleSheet.create({
    modalOuter: {
      width: '100%',
      maxWidth: 548,
      maxHeight: layout.outerMaxHeightPx,
      paddingHorizontal: 16,
      paddingVertical: 20,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0,
      }),
    },
    sheet: {
      width: '100%',
      maxWidth: 528,
      maxHeight: layout.sheetMaxHeightPx,
      minHeight: 0,
      borderRadius: 22,
      backgroundColor: theme.background.card,
      overflow: 'hidden',
      ...(Platform.OS === 'web'
        ? {
            boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
          }
        : {
            shadowColor: theme.shadow ?? '#000',
            shadowOffset: { width: 0, height: 10 },
            shadowOpacity: 0.12,
            shadowRadius: 20,
            elevation: 14,
          }),
    },
    scroll: {
      maxHeight: layout.sheetMaxHeightPx,
      minHeight: 0,
    },
    scrollContent: {
      paddingBottom: 28,
      paddingHorizontal: 20,
      paddingTop: 48,
    },
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      zIndex: 10,
      padding: 10,
      borderRadius: 999,
    },
    closeButtonPressed: {
      opacity: 0.65,
    },
    closeButtonHover: {
      opacity: 0.88,
    },
    loadingBox: {
      paddingVertical: 48,
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: 21,
      fontWeight: '700',
      color: theme.error,
      textAlign: 'center',
      letterSpacing: -0.4,
      marginBottom: 12,
      paddingHorizontal: 8,
    },
    copy: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.gray[600],
      textAlign: 'center',
      marginBottom: 20,
      paddingHorizontal: 4,
    },
    statusBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      alignSelf: 'center',
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 10,
      backgroundColor: theme.background.primary,
      borderWidth: 1,
      borderColor: theme.gray[200],
    },
    statusBadgeText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.gray[700],
    },
    deleteButton: {
      alignSelf: 'center',
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.error,
      backgroundColor: theme.background.card,
      minWidth: 160,
      alignItems: 'center',
      justifyContent: 'center',
    },
    deleteButtonDisabled: {
      opacity: 0.6,
    },
    deleteButtonPressed: {
      opacity: 0.75,
    },
    deleteButtonHover: {
      opacity: 0.88,
    },
    deleteButtonText: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.error,
    },
    confirmHint: {
      fontSize: 14,
      lineHeight: 20,
      color: theme.gray[600],
      textAlign: 'center',
      marginBottom: 10,
    },
    confirmPhrase: {
      fontWeight: '700',
      color: theme.gray[800],
    },
    input: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: 10,
      paddingVertical: 12,
      paddingHorizontal: 14,
      fontSize: 15,
      color: theme.gray[900],
      backgroundColor: theme.background.primary,
      marginBottom: 14,
    },
    errorText: {
      fontSize: 13,
      lineHeight: 18,
      color: theme.error,
      textAlign: 'center',
      marginBottom: 14,
    },
    buttonRow: {
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    cancelButton: {
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      minWidth: 110,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cancelButtonPressed: {
      opacity: 0.75,
    },
    cancelButtonHover: {
      opacity: 0.88,
    },
    cancelButtonText: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.gray[700],
    },
    confirmButton: {
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 10,
      backgroundColor: theme.error,
      minWidth: 150,
      alignItems: 'center',
      justifyContent: 'center',
    },
    confirmButtonDisabled: {
      opacity: 0.5,
    },
    confirmButtonPressed: {
      opacity: 0.8,
    },
    confirmButtonHover: {
      opacity: 0.9,
    },
    confirmButtonText: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.gray[900],
    },
  })
